/**
 * The funnel phase model — `phase.enter` / `phase.exit`.
 *
 * WHY THIS EXISTS: `bugs/schema.md` in the runbooks repo requires a
 * `denominator_description` for every bug, and today every one of them is
 * hand-built and re-argued ("scan-screen visits"). Nothing in `pwa_logs` says
 * *a user arrived at step X*. With this model every rate is
 * `count(phase.exit where outcome=X) / count(phase.enter)` — computed
 * identically for every phase, forever, over one composite index. Denominators
 * become a property of the schema instead of a per-investigation argument.
 *
 * INVARIANT: exactly one `phase.exit` for every `phase.enter`. Everything below
 * exists to hold that invariant on a mobile browser that can kill the page at
 * any moment.
 *
 * ── Why `hidden` emits the exit immediately ──────────────────────────────
 *
 * A mobile OS that backgrounds and later evicts a tab never fires `pagehide`.
 * Waiting for `pagehide` to decide "abandoned vs backgrounded" therefore loses
 * the exit row entirely for exactly the sessions we care about. So
 * `visibilitychange → hidden` emits `phase.exit { outcome: "backgrounded" }`
 * right away (the outbox catches it on the same tick), and if the tab comes
 * back, `visibilitychange → visible` re-enters the same phase with `attempt`
 * incremented (`resumeFromBackground`). The re-entry has to live HERE: the
 * screens enter phases from atoms and mount effects, none of which re-run on
 * a visibility change, so before it existed the rest of the step had no phase
 * and a later back-out emitted nothing.
 *
 * The cost is that a user who briefly checks a notification mid-alignment
 * produces two attempts rather than one. That is a fair trade — and it is
 * itself signal, since backgrounding mid-scan is a known way to lose a scan
 * (CHANGELOG [2.4.16]: the OS evicted the tab and it reloaded on return). Any
 * analysis that wants "one visit" should group by `journeyId` and take
 * `min(attempt)` / `max(attempt)` rather than counting enters.
 *
 * ── Durations use `performance.now()` ────────────────────────────────────
 *
 * Monotonic, and immune to the device-clock problems that make `timestamp`
 * unreliable (PLAN.md §4.2). A `durationMs` must never be able to go negative
 * because the user's phone re-synced its clock mid-scan.
 *
 * Payload contract and per-phase thresholds: `docs/telemetry/EVENTS.md`.
 */

import { logEvent } from "@/Utils/logger";
import { Events, Outcomes, type Outcome, type Phase } from "./events";
import { setCurrentPhase } from "./context";
import { onJourneyChange } from "./ids";
import { persistPending } from "./outbox";

interface ActivePhase {
  phase: Phase;
  startedAt: number;
  attempt: number;
  /** Guards against a double exit (e.g. `hidden` racing an explicit exit). */
  exited: boolean;
}

let active: ActivePhase | null = null;

/** Per-phase attempt counters, scoped to this page load. */
const attempts: { [phase: string]: number } = {};

let listenersBound = false;

/**
 * The phase closed by the last `visibilitychange → hidden`, so it can be
 * re-entered when the tab comes back. Cleared by `pagehide` (the page is going
 * away), by any other `enterPhase` (something else took over), and by the
 * re-entry itself.
 */
let backgroundedPhase: Phase | null = null;

/**
 * Bumped on EVERY `enterPhase` call — including calls that no-op because the
 * phase is already active. It is a "someone asserted this phase" counter, not a
 * transition counter, and that distinction is what makes `abandonOnUnmount`
 * work through a remount. See there.
 */
let phaseToken = 0;

export function getPhaseToken(): number {
  return phaseToken;
}

/**
 * Observers of phase transitions.
 *
 * Exists so the Stage-3 struggle accumulators can hang off the phase model
 * rather than off a screen. That matters for correctness, not tidiness: a phase
 * closed by `pagehide` or `visibilitychange` never runs a component cleanup, and
 * abandoned/backgrounded sessions are exactly the ones a struggle record is
 * worth having.
 */
type PhaseObserver = (
  kind: "enter" | "exit",
  phase: Phase,
  outcome: Outcome | null,
  durationMs: number | null,
  attempt: number | null
) => void;

const observers: PhaseObserver[] = [];

export function onPhaseTransition(cb: PhaseObserver): () => void {
  observers.push(cb);
  return () => {
    const i = observers.indexOf(cb);
    if (i !== -1) observers.splice(i, 1);
  };
}

/** Never lets an observer's failure break the phase model or the app. */
function notify(
  kind: "enter" | "exit",
  phase: Phase,
  outcome: Outcome | null,
  durationMs: number | null,
  attempt: number | null
): void {
  for (let i = 0; i < observers.length; i++) {
    try {
      observers[i](kind, phase, outcome, durationMs, attempt);
    } catch {
      /* an accumulator must never take the phase model down with it */
    }
  }
}

/**
 * Enter a phase.
 *
 * If another phase is active it is exited first with `advanced` — moving from
 * `alignment` to `rotation` IS the success case for `alignment`, and making
 * that implicit means a forgotten `exitPhase` call cannot silently destroy a
 * denominator.
 *
 * Re-entering the phase that is already active is a no-op, so a React effect
 * that runs twice (StrictMode, a remount) cannot double-count arrivals. This is
 * the bounding rule from PLAN.md §5.2 — one stuck user must be one row.
 */
export function enterPhase(phase: Phase, data?: Record<string, unknown>): void {
  try {
    phaseToken++;
    backgroundedPhase = null;
    if (active && active.phase === phase) return;
    if (active) exitPhase(active.phase, Outcomes.ADVANCED);

    const attempt = (attempts[phase] = (attempts[phase] || 0) + 1);
    active = {
      phase,
      startedAt: performance.now(),
      attempt,
      exited: false,
    };
    setCurrentPhase(phase);

    logEvent(Events.PHASE_ENTER, `[telemetry] enter ${phase}`, {
      phase,
      data: { attempt, ...(data || {}) },
    });
    notify("enter", phase, null, null, attempt);
  } catch {
    /* telemetry must never break the app */
  }
}

/**
 * Leave a phase. No-op unless `phase` is the one currently active, so a late or
 * duplicated exit cannot emit a second row or a negative duration.
 */
export function exitPhase(
  phase: Phase,
  outcome: Outcome,
  data?: Record<string, unknown>
): void {
  try {
    if (!active || active.phase !== phase || active.exited) return;
    active.exited = true;

    const durationMs = Math.max(0, Math.round(performance.now() - active.startedAt));
    const attempt = active.attempt;
    active = null;
    setCurrentPhase(null);

    logEvent(Events.PHASE_EXIT, `[telemetry] exit ${phase} (${outcome})`, {
      phase,
      outcome,
      durationMs,
      data: { attempt, ...(data || {}) },
    });
    // AFTER the exit row, so a summary emitted by an observer lands at the next
    // `seq` and reads as belonging to the phase that just closed.
    notify("exit", phase, outcome, durationMs, attempt);
  } catch {
    /* telemetry must never break the app */
  }
}

/**
 * Exit whatever phase is active, whichever it is.
 *
 * For terminal transitions where the caller knows the OUTCOME but not which
 * phase reached it — a rotation error can surface while `rotation` or
 * `alignment` is active, and a screen unmount can happen from any of them.
 * Naming the phase at those call sites would silently no-op on the branch the
 * author did not think of, losing the exit row and with it the denominator.
 */
export function exitActivePhase(
  outcome: Outcome,
  data?: Record<string, unknown>
): void {
  if (!active) return;
  exitPhase(active.phase, outcome, data);
}

/** The phase currently in flight, if any. */
export function getActivePhase(): Phase | null {
  return active ? active.phase : null;
}

/**
 * Close whatever is open, because the page is going away or being hidden.
 *
 * `persistPending()` runs immediately afterwards so the row just emitted is in
 * the outbox before the page can be killed — the Firestore write it started
 * will almost certainly not resolve in time. This is the whole reason the
 * outbox exists.
 */
function closeForTermination(outcome: Outcome): void {
  if (!active) return;
  const phase = active.phase;
  exitPhase(phase, outcome);
  backgroundedPhase = outcome === Outcomes.BACKGROUNDED ? phase : null;
  persistPending();
}

/**
 * The tab came back. The screen that owns the phase is still mounted and
 * nothing it keys on has changed, so nobody else will re-enter — the screens
 * enter phases from atoms and mount effects, none of which re-run on a
 * visibility change. Without this, the rest of the step has no phase and a
 * later back-out emits nothing.
 */
function resumeFromBackground(): void {
  const phase = backgroundedPhase;
  backgroundedPhase = null;
  if (!phase || active) return;
  enterPhase(phase);
}

/**
 * Bind lifecycle hooks. Idempotent. Call once at bootstrap.
 *
 * `pagehide` covers real navigations and iOS bfcache entry; `visibilitychange`
 * is the only signal a backgrounded-then-evicted mobile tab ever gives. Neither
 * alone is sufficient. `beforeunload` is deliberately unused — unreliable on
 * mobile, and registering it can disqualify the page from the bfcache.
 */
export function bindPhaseLifecycle(): void {
  if (listenersBound || typeof window === "undefined") return;
  listenersBound = true;
  try {
    window.addEventListener("pagehide", () => {
      closeForTermination(Outcomes.ABANDONED);
      backgroundedPhase = null;
    });
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        closeForTermination(Outcomes.BACKGROUNDED);
      } else if (document.visibilityState === "visible") {
        resumeFromBackground();
      }
    });
    // A journey boundary (submit / discard) closes whatever is open, inside the
    // journey that opened it. Runs before the id rotates — see ids.ts.
    onJourneyChange(() => {
      if (active) exitPhase(active.phase, Outcomes.ABANDONED);
      backgroundedPhase = null;
    });
  } catch {
    /* ignore */
  }
}

/**
 * Exit `phases` as ABANDONED on unmount — but only if this is a REAL unmount.
 *
 * A component cleanup cannot tell "the user navigated away" from "React is
 * remounting me". React 18 StrictMode unmounts and remounts every component in
 * development, and a plain `exitActivePhase(ABANDONED)` in a cleanup therefore
 * emitted a spurious abandonment on every single scan-screen visit —
 * corrupting the one metric this whole model exists to measure. Legitimate
 * remounts (a re-keyed parent, a route transition) would do the same in
 * production.
 *
 * So the exit is DEFERRED by a task and cancelled if anything asserts a phase
 * in the meantime. On a remount the new `enterPhase` bumps the token (even
 * though it no-ops, because the phase is already active) and the deferred exit
 * stands down. On a real navigation nothing bumps it and the abandonment is
 * recorded. Navigating to a screen that opens its OWN phase also bumps it, and
 * that path is already covered by `enterPhase`'s auto-exit with `advanced`.
 *
 * Returns the cleanup's cleanup — call it if the caller unmounts again before
 * the task runs.
 */
export function abandonOnUnmount(phases: readonly string[]): () => void {
  const token = phaseToken;
  const timer = setTimeout(() => {
    if (phaseToken !== token) return; // something re-asserted a phase — not an abandonment
    const current = getActivePhase();
    if (current && phases.indexOf(current) !== -1) {
      exitActivePhase(Outcomes.ABANDONED);
    }
  }, 0);
  return () => clearTimeout(timer);
}

/** Test-only. */
export function __resetPhasesForTest(): void {
  phaseToken = 0;
  observers.length = 0;
  active = null;
  backgroundedPhase = null;
  listenersBound = false;
  for (const k of Object.keys(attempts)) delete attempts[k];
  setCurrentPhase(null);
}
